import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../../shared/enums/request-status';
import type { AvailableSlot } from '../../types';
import {
  signupRequest,
  verifyUserVerificationCodeRequest,
  resendUserVerificationCodeRequest,
  onboardingDetailsRequest,
  submitOnboardingRequest,
  getAvailableSlotsRequest,
  getAnonymousUserHashRequest,
} from './extra-actions';

type RequestState = {
  status: RequestStatus;
  message: string;
  error: any;
  showError?: boolean;
};

type State = {
  signupRequest: RequestState;
  verifyUserVerificationCodeRequest: RequestState;
  resendUserVerificationCodeRequest: RequestState;
  onBoardingDetailsRequest: RequestState;
  onboardingSubmitRequest: RequestState;
  availableSlotsRequest: RequestState;
  availableSlots: AvailableSlot[];
  availableSlotsTimezone: string | null;
  availableSlotsLastPageEmpty: boolean;
  onboardingCompleted: boolean;
  email: string;
  token: string;
  trackingId: string;
  firstName: string;
  lastName: string;
  canResendUserVerificationCode: boolean;
  isPlanSynced: boolean;
  anonymousUserHashStatus: RequestStatus;
  anonymousUserHashMessage: string;
  anonymousUserHashError: any;
  anonymousUserHash: string | null;
  anonymousUserId: string | null;
};

const initialState: State = {
  signupRequest: {
    status: RequestStatus.Ideal,
    message: '',
    error: '',
    showError: false,
  },
  verifyUserVerificationCodeRequest: {
    status: RequestStatus.Ideal,
    message: '',
    error: '',
    showError: false,
  },
  resendUserVerificationCodeRequest: {
    status: RequestStatus.Ideal,
    message: '',
    error: '',
  },
  onBoardingDetailsRequest: {
    status: RequestStatus.Ideal,
    message: '',
    error: '',
  },
  onboardingSubmitRequest: {
    status: RequestStatus.Ideal,
    message: '',
    error: '',
  },
  availableSlotsRequest: {
    status: RequestStatus.Ideal,
    message: '',
    error: '',
  },
  availableSlots: [],
  availableSlotsTimezone: null,
  availableSlotsLastPageEmpty: false,
  onboardingCompleted: false,
  email: '',
  token: '',
  trackingId: null,
  firstName: null,
  lastName: null,
  canResendUserVerificationCode: true,
  isPlanSynced: false,
  anonymousUserHashStatus: RequestStatus.Ideal,
  anonymousUserHashMessage: '',
  anonymousUserHashError: null,
  anonymousUserHash: null,
  anonymousUserId: null,
};

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    hideSignupRequestError: (state) => {
      state.signupRequest.showError = false;
    },
    hideVerifyUserVerificationCodeRequestError: (state) => {
      state.verifyUserVerificationCodeRequest.showError = false;
    },
    resetIsPlanSynced: (state) => {
      state.isPlanSynced = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signupRequest.pending, (state) => {
      state.signupRequest.status = RequestStatus.Pending;
      state.signupRequest.showError = false;
    });
    builder.addCase(signupRequest.fulfilled, (state, action) => {
      state.signupRequest.status = RequestStatus.Succeeded;
      state.signupRequest.message = action.payload.message;
      state.isPlanSynced = action.payload.payload.planAssignment;
    });
    builder.addCase(signupRequest.rejected, (state, action) => {
      state.signupRequest.status = RequestStatus.Failed;
      state.signupRequest.error = action.payload;
      state.signupRequest.showError = !action.payload.isHandled;
    });

    builder.addCase(onboardingDetailsRequest.pending, (state) => {
      state.onBoardingDetailsRequest.status = RequestStatus.Pending;
      state.onBoardingDetailsRequest.showError = false;
    });
    builder.addCase(onboardingDetailsRequest.fulfilled, (state, action) => {
      state.onBoardingDetailsRequest.status = RequestStatus.Succeeded;
      state.onBoardingDetailsRequest.message = action.payload.message;
      state.firstName = action.payload.payload.firstName;
      state.lastName = action.payload.payload.lastName;
      state.email = action.payload.payload.email;
    });
    builder.addCase(onboardingDetailsRequest.rejected, (state, action) => {
      state.onBoardingDetailsRequest.status = RequestStatus.Failed;
      state.onBoardingDetailsRequest.error = action.payload;
      state.onBoardingDetailsRequest.showError = !action.payload.isHandled;
    });

    builder.addCase(submitOnboardingRequest.pending, (state) => {
      state.onboardingSubmitRequest.status = RequestStatus.Pending;
      state.onboardingSubmitRequest.error = '';
    });
    builder.addCase(submitOnboardingRequest.fulfilled, (state) => {
      state.onboardingSubmitRequest.status = RequestStatus.Succeeded;
      state.onboardingCompleted = true;
    });
    builder.addCase(submitOnboardingRequest.rejected, (state, action) => {
      state.onboardingSubmitRequest.status = RequestStatus.Failed;
      state.onboardingSubmitRequest.error = action.payload;
    });

    builder.addCase(getAvailableSlotsRequest.pending, (state) => {
      state.availableSlotsRequest.status = RequestStatus.Pending;
      state.availableSlotsRequest.message = '';
      state.availableSlotsRequest.error = null;
    });
    builder.addCase(getAvailableSlotsRequest.fulfilled, (state, action) => {
      state.availableSlotsRequest.status = RequestStatus.Succeeded;
      const payload = action.payload as any;
      const data = payload?.data ?? payload?.payload ?? payload ?? {};
      const rawSlots = Array.isArray(payload)
        ? payload
        : data.slots ?? payload?.slots ?? [];
      const normalized = rawSlots
        .map((s: any) => ({
          time: s.time ?? s.startTime ?? '',
          inviteesRemaining: s.inviteesRemaining ?? 0,
        }))
        .filter((s) => s.time);
      const isPaginated =
        action.meta.arg &&
        typeof action.meta.arg === 'object' &&
        action.meta.arg.startTime;
      if (isPaginated) {
        if (normalized.length === 0) {
          state.availableSlotsLastPageEmpty = true;
        } else {
          const existingTimes = new Set(
            state.availableSlots.map((s) => s.time),
          );
          const newSlots = normalized.filter((s) => !existingTimes.has(s.time));
          state.availableSlots = [...state.availableSlots, ...newSlots].sort(
            (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
          );
        }
      } else {
        state.availableSlotsLastPageEmpty = false;
        state.availableSlotsTimezone =
          data.timezone ?? payload?.timezone ?? null;
        state.availableSlots = normalized;
      }
    });
    builder.addCase(getAvailableSlotsRequest.rejected, (state, action) => {
      state.availableSlotsRequest.status = RequestStatus.Failed;
      state.availableSlotsRequest.error = action.payload;
      state.availableSlotsRequest.message = action.payload?.message;
      state.availableSlots = [];
      state.availableSlotsTimezone = null;
      state.availableSlotsLastPageEmpty = false;
    });

    builder.addCase(verifyUserVerificationCodeRequest.pending, (state) => {
      state.verifyUserVerificationCodeRequest.status = RequestStatus.Pending;
      state.verifyUserVerificationCodeRequest.showError = false;
    });
    builder.addCase(
      verifyUserVerificationCodeRequest.fulfilled,
      (state, action) => {
        state.verifyUserVerificationCodeRequest.status =
          RequestStatus.Succeeded;
        state.verifyUserVerificationCodeRequest.message =
          action.payload.message;
        state.token = action.payload.payload.accessToken;
        state.trackingId = action.payload.payload.trackingId;
        state.firstName = action.payload.payload.firstName;
        state.lastName = action.payload.payload.lastName;
        state.email = action.payload.payload.email;
      },
    );
    builder.addCase(
      verifyUserVerificationCodeRequest.rejected,
      (state, action) => {
        state.verifyUserVerificationCodeRequest.status = RequestStatus.Failed;
        state.verifyUserVerificationCodeRequest.error = action.payload;
        state.verifyUserVerificationCodeRequest.showError = !action.payload
          .isHandled;
      },
    );
    builder.addCase(resendUserVerificationCodeRequest.pending, (state) => {
      state.resendUserVerificationCodeRequest.status = RequestStatus.Pending;
      state.resendUserVerificationCodeRequest.error = null;
    });
    builder.addCase(
      resendUserVerificationCodeRequest.fulfilled,
      (state, action) => {
        state.resendUserVerificationCodeRequest.status =
          RequestStatus.Succeeded;
        state.resendUserVerificationCodeRequest.message =
          action.payload.message;
        state.canResendUserVerificationCode =
          action.payload.payload.canResendUserVerificationCode;
      },
    );
    builder.addCase(
      resendUserVerificationCodeRequest.rejected,
      (state, action) => {
        state.resendUserVerificationCodeRequest.status = RequestStatus.Failed;
        state.resendUserVerificationCodeRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    builder.addCase(getAnonymousUserHashRequest.pending, (state) => {
      state.anonymousUserHashStatus = RequestStatus.Pending;
      state.anonymousUserHashError = null;
    });
    builder.addCase(getAnonymousUserHashRequest.fulfilled, (state, action) => {
      state.anonymousUserHashStatus = RequestStatus.Succeeded;
      state.anonymousUserHashMessage = action.payload.message || '';
      state.anonymousUserHash = action.payload.payload?.userHash || null;
      state.anonymousUserId =
        action.payload.payload?.anonymousUserId != null
          ? String(action.payload.payload.anonymousUserId)
          : null;
    });
    builder.addCase(getAnonymousUserHashRequest.rejected, (state, action) => {
      state.anonymousUserHashStatus = RequestStatus.Failed;
      state.anonymousUserHashError = action.payload;
    });
  },
});

export const {
  hideSignupRequestError,
  hideVerifyUserVerificationCodeRequestError,
  resetIsPlanSynced,
} = signupSlice.actions;
export {
  signupRequest,
  verifyUserVerificationCodeRequest,
  resendUserVerificationCodeRequest,
  submitOnboardingRequest,
  getAvailableSlotsRequest,
  getAnonymousUserHashRequest,
};
export default signupSlice.reducer;
